import { Colors, Component } from '@lessonup/teaching-core';
import React, { useEffect, useRef, useState } from 'react';
import { ImageUtils } from '../../../../utils/ImageUtils';
import { secondsToPrettyTime } from '../../../../utils/TimeUtils';
import ComponentContainer from '../componentContainer/ComponentContainer';
import './AudioComponentView.less';

interface AudioComponentViewProps {
  component: Component.Audio;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- auto-generated TECH-258
  onClickHandler?: Function;
  defaultState?: string;
  isThumb?: boolean;
}

interface TitleProps {
  title: string | undefined;
}

const AudioComponentView: React.FC<AudioComponentViewProps> = (props) => {
  const [isAudioPlaying, setIsAudioPlaying] = useState<boolean>(false);
  const [showAudioDuration, setShowAudioDuration] = useState<boolean>(false);
  const [audioTime, setAudioTime] = useState<number>();
  const [isAudioLoaded, setIsAudioLoaded] = useState<boolean>(false);

  const componentTypeClasses = () => {
    const { position } = props.component;
    const positionX = position.left > 50 ? 'pos-right' : 'pos-left';
    const positionY = position.top > 50 ? 'pos-bottom' : 'pos-top';
    return `view-component-audio ${positionX} ${positionY}`;
  };
  const { settings } = props.component;
  const audioFile = useRef<HTMLAudioElement>(null);

  const audioProps: AudioHandlerProps = {
    audioFile: audioFile.current,
    isAudioPlaying,
    setIsAudioPlaying,
    setShowAudioDuration,
    showAudioDuration,
    setAudioTime,
  };

  useEffect(() => {
    if (audioFile.current && !isAudioLoaded && !props.isThumb && audioFile.current.readyState < 4) {
      audioFile.current
        .play()
        .catch(() => {
          // Error won't be noticed by the user and error handling is not needed.
        })
        .finally(() => audioFile?.current?.pause());

      setIsAudioLoaded(true);
    }
  }, [audioFile, isAudioLoaded, props.isThumb]);

  const handleTimeUpdate = () => {
    if (audioFile.current) calculateAudioTime(audioProps);
  };

  return (
    <div>
      <ComponentContainer
        {...props}
        componentTypeClasses={componentTypeClasses()}
        isInteractive={!props.isThumb}
        onClickHandler={(e) => audioHandler(e, audioProps)}
      >
        <Title title={settings.title} />
        <div className="border" data-grippy="move"></div>
        {settings.image ? (
          <IconComponentImage image={settings.image} imageSize={settings.imageSize} color={settings.color} />
        ) : (
          <Icon color={settings.color} icon={settings.icon} />
        )}
        {settings.url ? (
          <>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- auto-generated TECH-258 */}
            <audio ref={audioFile} onTimeUpdate={handleTimeUpdate}>
              <source src={settings.url} type={settings.contentType} />
            </audio>
            {audioFile.current && showAudioDuration ? (
              <p className="audio-duration font-courier paused">
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- auto-generated TECH-258 */}
                <span
                  className="icon icon-undo"
                  data-role="reset-player"
                  onClick={(e) => resetAudio(e, audioProps)}
                ></span>
                {secondsToPrettyTime(audioTime)}
              </p>
            ) : null}
          </>
        ) : null}
      </ComponentContainer>
    </div>
  );
};

export default AudioComponentView;

function Title(props: TitleProps) {
  if (!props.title) return null;
  return (
    <div className="audio-title" data-grippy="move">
      {props.title}
    </div>
  );
}

function Icon(props) {
  // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
  const iconClass = props.icon ? 'icon-' + props.icon : 'icon-plus';
  // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
  const darkClass = Colors.needsDarkIcon(props.color) ? 'icon-dark' : '';

  // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
  const colorClass = props.color ? `bg-${props.color}` : '';

  return (
    // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
    <div className={`core ${darkClass} ${colorClass}`} style={{ backgroundColor: props.color }} data-grippy="move">
      <div className={`core-icon icon ${darkClass} ${iconClass}`} data-grippy="move"></div>
    </div>
  );
}

function IconComponentImage(props) {
  // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
  const darkClass = Colors.needsDarkIcon(props.color) ? 'icon-dark' : '';

  // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
  const colorClass = props.color ? `bg-${props.color}` : '';

  const style = () => ({
    // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
    backgroundImage: ImageUtils.urlString(ImageUtils.checkImageUrl(props.image)),
    // eslint-disable-next-line react/prop-types -- auto-generated TECH-258
    backgroundSize: props.imageSize,
  });

  return (
    <div className={`core ${darkClass} ${colorClass}`} data-grippy="move">
      <div className="core-image" style={style()} data-grippy="move"></div>
    </div>
  );
}

interface AudioHandlerProps {
  isAudioPlaying: boolean;
  setIsAudioPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  showAudioDuration: boolean;
  setShowAudioDuration: React.Dispatch<React.SetStateAction<boolean>>;
  audioFile: HTMLAudioElement | null;
  setAudioTime: React.Dispatch<React.SetStateAction<number>>;
}

const playAudio = ({ audioFile, setIsAudioPlaying }: AudioHandlerProps) => {
  if (!audioFile) return;
  audioFile.play();
  setIsAudioPlaying(true);
};

const pauseAudio = ({ audioFile, setIsAudioPlaying }: AudioHandlerProps) => {
  if (!audioFile) return;
  audioFile.pause();
  setIsAudioPlaying(false);
};

const audioHandler = (event: React.MouseEvent, audioProps: AudioHandlerProps) => {
  const { isAudioPlaying, showAudioDuration, setShowAudioDuration, audioFile } = audioProps;
  if (!audioFile) return;

  if (event && !isAudioPlaying) {
    playAudio(audioProps);
  } else {
    pauseAudio(audioProps);
  }
  if (!showAudioDuration) setShowAudioDuration(true);
};

const resetAudio = (event: React.MouseEvent | undefined, audioProps: AudioHandlerProps) => {
  const { setIsAudioPlaying, setShowAudioDuration, audioFile } = audioProps;
  event?.stopPropagation();
  if (!audioFile) return;
  pauseAudio(audioProps);
  setShowAudioDuration(false);
  setIsAudioPlaying(false);
  audioFile.currentTime = 0;
};

const calculateAudioTime = (audioProps: AudioHandlerProps) => {
  const { audioFile, setAudioTime } = audioProps;
  if (!audioFile) return;
  const { currentTime, duration } = audioFile;
  const totalSeconds = duration - currentTime;

  if (totalSeconds === 0) {
    resetAudio(undefined, audioProps);
  }

  return setAudioTime(totalSeconds);
};
